import React, { useEffect } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useUser } from './context/UserContext';

// Import layout components
import Header from './components/layout/Header';
import SidebarMenu from './components/layout/SidebarMenu';
import NotificationsPanel from './components/layout/NotificationsPanel';
import AssetPreloader from './components/layout/AssetPreloader';

// Import modals
import ChooseYourPathModal from './components/modals/ChooseYourPathModal';

// Import page components
import HomePage from './pages/HomePage';
import WalletFinancePage from './pages/WalletFinancePage';
import WalletTransactionsPage from './pages/WalletTransactionsPage';
import WalletSecurityPage from './pages/WalletSecurityPage';
import SettingsProfilePage from './pages/SettingsProfilePage';
import SettingsNotificationsPage from './pages/SettingsNotificationsPage';
import SettingsWalletPage from './pages/SettingsWalletPage';
import SettingsSecurityPage from './pages/SettingsSecurityPage';
import SettingsPrivacyPage from './pages/SettingsPrivacyPage';
import SettingsDisplayPage from './pages/SettingsDisplayPage';
import SettingsAdvancedPage from './pages/SettingsAdvancedPage';
import LoginPage from './pages/auth/LoginPage';

// Import debug components - only used in development
import Debugger from './components/debug/Debugger';

import './App.scss';
import { UserProvider } from './context/UserContext';
import { AuthProvider } from './context/AuthContext';

// Protected route component that redirects to login if not authenticated
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  
  // If still loading authentication state, return nothing yet
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }
  
  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // If authenticated, render the children
  return <>{children}</>;
};

// Separate component for the app content that can use hooks
const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { fetchUserData, fetchCharacters } = useUser();
  
  useEffect(() => {
    // Any global initialization code that was in main.js can be moved here
    document.documentElement.classList.add('js');
    
    // Log that the app is initialized
    console.log('App initialized');
  }, []);
  
  // Redirect to login if user is not authenticated
  useEffect(() => {
    if (!loading && !user && location.pathname !== '/login') {
      navigate('/login');
    }
  }, [user, loading, location.pathname, navigate]);
  
  // Fetch user data and characters when user is authenticated
  useEffect(() => {
    if (user) {
      console.log('User authenticated, fetching user data and characters');
      
      // Fetch user profile first
      fetchUserData().then(() => {
        console.log('User profile fetched, now fetching characters');
        // Only fetch characters after user profile is loaded
        fetchCharacters();
      }).catch(error => {
        console.error('Error fetching user data:', error);
      });
    }
  }, [user]);
  
  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // If on login page, just show the login page
  if (location.pathname === '/login') {
    return <LoginPage />;
  }

  // If loading or not authenticated, show nothing (redirection happens in useEffect)
  if (loading || !user) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <>
      {/* Asset Preloader */}
      <AssetPreloader />
      
      {/* Choose your path modal */}
      <ChooseYourPathModal />
      
      {/* Main container */}
      <div className="l-main-container">
        {/* Header */}
        <Header />
        
        {/* Main content */}
        <div className="l-main-content">
          {/* Sidebar Menu - Placed OUTSIDE the container for proper positioning */}
          <SidebarMenu />
          
          {/* Notifications Panel - Placed OUTSIDE the container for proper positioning */}
          <NotificationsPanel />
          
          <div className="container">
            {/* Content */}
            <div className="s-main-content">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/wallet-finance" element={<WalletFinancePage />} />
                <Route path="/wallet-transactions" element={<WalletTransactionsPage />} />
                <Route path="/wallet-security" element={<WalletSecurityPage />} />
                <Route path="/settings-profile" element={<SettingsProfilePage />} />
                <Route path="/settings-notifications" element={<SettingsNotificationsPage />} />
                <Route path="/settings-wallet" element={<SettingsWalletPage />} />
                <Route path="/settings-security" element={<SettingsSecurityPage />} />
                <Route path="/settings-privacy" element={<SettingsPrivacyPage />} />
                <Route path="/settings-display" element={<SettingsDisplayPage />} />
                <Route path="/settings-advanced" element={<SettingsAdvancedPage />} />
                <Route path="/login" element={<LoginPage />} />
              </Routes>
            </div>
          </div>
        </div>
      </div>
      
      {/* Debug panel - only visible in development */}
      {process.env.NODE_ENV === 'development' && <Debugger />}
    </>
  );
};

// Main App component just sets up providers
const App = () => {
  return (
    <AuthProvider>
      <UserProvider>
        <AppContent />
      </UserProvider>
    </AuthProvider>
  );
};

export default App; 